import { getGiveawayNumberInputProps } from '@cart-services/core/couponService'
import { type SelectedCouponGiveawayProductMap } from '@cart-services/types'
import { wordpressCartGiveawayEvents } from '@dni-analysis'
import { type ECommerceProduct } from '@hooks-api/types/dataModel/products'
import { Button } from '@pets-ui/Button/Button'
import { DialogActions } from '@pets-ui/DialogActions/DialogActions'
import { DialogContent } from '@pets-ui/DialogContent/DialogContent'
import { DialogHeader } from '@pets-ui/DialogHeader/DialogHeader'
import { Divider } from '@pets-ui/Divider/Divider'
import { HStack } from '@pets-ui/HStack/HStack'
import { GiftOutlined } from '@pets-ui/Icons'
import { ResponsiveDialog } from '@pets-ui/ResponsiveDialog/ResponsiveDialog'
import { Text } from '@pets-ui/Text/Text'
import { type MobileMediaQuery } from '@pets-ui/tokens/breakpoints.stylex'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { elevationTop } from '@pets-ui/tokens/elevation.stylex'
import { useResponsive } from '@pets-ui/useResponsive/useResponsive'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { sumOfArray } from '@utils/sumOfArray'
import { type ReactNode, useState, useMemo, useCallback } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import cartUiI18nInstance from '../i18n/i18nConfig'
import { GiveawayNumberInput } from './GiveawayNumberInput'
const MOBILE: MobileMediaQuery = '@media (max-width: 480px)'

const styles = stylex.create({
  dialogActions: {
    boxShadow: elevationTop['elevation8'],
  },
  dialogContent: {
    marginTop: 16,
    overflowY: 'hidden',
  },
  giveawayNumberInputContainer: {
    gap: 12,
    height: {
      [MOBILE]: 280,
      default: 320,
    },
    overflowX: 'hidden',
    overflowY: 'auto',
    paddingBottom: {
      [MOBILE]: 0,
      default: 32,
    },
  },
})

export const DIALOG_SELECT_GIVEAWAY = 'DIALOG_SELECT_GIVEAWAY'

export interface DialogSelectGiveawayContentProps {
  onClose?: () => void
  options?: ECommerceProduct[]
  /** 是否可累贈 */
  isRepeatable: boolean
  /** 是否為全送，使用者無需選擇贈品 */
  isGiveAll: boolean
  /** 贈品庫存不足，部分贈品已贈完 */
  isStockPartiallyNotEnough: boolean
  /** 贈品可選數量，已透過庫存數量計算 */
  accumulatedGiftSelectionCount: number
  title?: ReactNode
  onSubmit?: (selectedCouponGiveawayProductMap: SelectedCouponGiveawayProductMap) => void
  defaultGiveawayValues?: SelectedCouponGiveawayProductMap
  errorMessage?: string
  remainingCriterionCount?: number
  isToCheckoutPage?: boolean
  couponCriterionId?: number
  /** 區分同一 criterion 展開的多張卡片（兌換券一票一卡），決定哪張卡顯示 active 邊框 */
  instanceKey?: string
  isMobile?: boolean
  onClickSubmitWithAnotherGiftSendEvent?: () => void
  onClickSubmitSendEvent?: () => void
  onClickSubmitWithToCheckoutSendEvent?: () => void
}

const DialogSelectGiveawayContent = (props: DialogSelectGiveawayContentProps) => {
  const {
    onClose,
    options,
    title,
    onSubmit,
    defaultGiveawayValues,
    isGiveAll,
    accumulatedGiftSelectionCount,
    remainingCriterionCount,
    isToCheckoutPage,
    errorMessage,
  } = props
  const [selectedCouponGiveawayProductMap, setSelectedCouponGiveawayProductMap] = useState<SelectedCouponGiveawayProductMap>(defaultGiveawayValues || {})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { t } = useTranslation('cartUi', { i18n: cartUiI18nInstance })

  const handleChange = (id: ECommerceProduct['id'], projectCode: ECommerceProduct['project_code'], sku: ECommerceProduct['sku']) => (value?: number) => {
    setSelectedCouponGiveawayProductMap(prev => ({
      ...prev,
      [id]: {
        product_id: id,
        sku,
        project_code: projectCode,
        quantity: value ?? 0,
      },
    }))
  }
  const {
    sendGiftPopupSelectNextGift,
    sendGiftPopupConfirmBtn,
    sendGiftPopupGoCheckoutBtn,
    sendGiftPopupQuantityPlusBtn,
    sendGiftPopupQuantityMinusBtn,
  } = wordpressCartGiveawayEvents()

  /**
   * NOTE: 因為 merchant 也是共用同一個元件，且不會傳入 remainingCriterionCount，因此 merchant 會是 -1
   */
  const displayRemainingCriterionCount = (remainingCriterionCount ?? 0) - 1
  const isDisplayRemainingCriterionCountGreaterThanZero = displayRemainingCriterionCount > 0
  const isDisplayRemainingCriterionCountEqualZero = displayRemainingCriterionCount === 0
  /** NOTE: For merchant */
  const isDisplayRemainingCriterionCountLessThanZero = displayRemainingCriterionCount < 0

  const handleSubmitSendEvent = useCallback(() => {
    if (isDisplayRemainingCriterionCountGreaterThanZero) {
      sendGiftPopupSelectNextGift()
    }
    if (isDisplayRemainingCriterionCountEqualZero) {
      sendGiftPopupConfirmBtn()
    }
    if (isDisplayRemainingCriterionCountLessThanZero) {
      sendGiftPopupGoCheckoutBtn()
    }
  }, [isDisplayRemainingCriterionCountEqualZero, isDisplayRemainingCriterionCountGreaterThanZero, isDisplayRemainingCriterionCountLessThanZero, sendGiftPopupConfirmBtn, sendGiftPopupGoCheckoutBtn, sendGiftPopupSelectNextGift])

  const handleSubmit = () => {
    handleSubmitSendEvent()
    setIsSubmitting(true)
    try {
      /** 不在此關閉 dialog，由 onSubmit 控制 */
      onSubmit?.(selectedCouponGiveawayProductMap)
    }
    catch (error) {
      console.error(error)
    }
    finally {
      setIsSubmitting(false)
    }
  }

  const totalSelectedQuantity = sumOfArray(Object.values(selectedCouponGiveawayProductMap), (acc, cur) => acc + cur.quantity)

  const sortedOptionsWithProps = useMemo(() => {
    if (!options) return []

    const optionsWithProps = options.map((option) => {
      const props = getGiveawayNumberInputProps({
        option,
        selectedCouponGiveawayProductMap,
        isGiveAll,
        accumulatedGiftSelectionCount,
        totalSelectedQuantity,
      })
      return { option, props }
    })

    return [...optionsWithProps].sort((a, b) => {
      const aHasStock = (a.props.stock ?? 0) > 0
      const bHasStock = (b.props.stock ?? 0) > 0

      if (aHasStock && !bHasStock) return -1
      if (!aHasStock && bHasStock) return 1
      return 0
    })
  }, [options, selectedCouponGiveawayProductMap, isGiveAll, accumulatedGiftSelectionCount, totalSelectedQuantity])

  const remainingGiftCount = accumulatedGiftSelectionCount - totalSelectedQuantity
  const currentSelectedQuantity = sumOfArray(Object.values(selectedCouponGiveawayProductMap), (acc, cur) => acc + cur.quantity)
  const isSelectedAll = currentSelectedQuantity === accumulatedGiftSelectionCount
  const isReadOnly = !!selectedCouponGiveawayProductMap[sortedOptionsWithProps[0]?.option?.id ?? 0]?.isReadyOnly

  return (
    <>
      <DialogHeader title={t('cartUi.selectGifts', { defaultValue: '選擇贈品' })} onClose={onClose} />
      <Divider />
      <DialogContent rootStyle={styles.dialogContent}>
        <VStack gap={3} overflow="hidden">
          <Text variant="h5" lineClamp={2}>{title}</Text>
          <HStack alignItems="center" gap={1}>
            <GiftOutlined fontSize={16} color={colors.red600} />
            {remainingGiftCount > 0 && !isReadOnly && (
              <Text component="span" color={colors.grey700}>
                <Trans
                  i18n={cartUiI18nInstance}
                  i18nKey="cartUi.giveawayDialog.notYetCompletedTooltip"
                  defaults="還需選 <Text>{{remainingGiftCount}}</Text> 份好禮！"
                  values={{
                    remainingGiftCount,
                  }}
                  components={{
                    Text: <Text variant="subtitle" component="span" color={colors.red600} />,
                  }}
                />
              </Text>
            )}
            {(remainingGiftCount === 0 || isReadOnly) && (
              <Text component="span" color={colors.grey700}>
                {t('cartUi.giveawayDialog.completedTooltip', {
                  count: currentSelectedQuantity,
                  defaultValue: '挑選完畢，獲得 {{count}} 份好禮！',
                })}
              </Text>
            )}
          </HStack>
          {errorMessage && <Text color={colors.red600}>{errorMessage}</Text>}
          <VStack rootStyle={styles.giveawayNumberInputContainer}>
            {sortedOptionsWithProps?.map(({ option, props }) => {
              const { maxSelectableQuantity, stock, disabled, selfSelectedQuantity } = props
              const notSelected = (selfSelectedQuantity === 0 && remainingGiftCount === 0) || stock === 0
              const isReadyOnly = !!selectedCouponGiveawayProductMap[option.id]?.isReadyOnly
              return (
                <GiveawayNumberInput
                  isReadOnly={isReadyOnly}
                  key={option.id}
                  thumbnail={option.thumbnail || ''}
                  name={option.name}
                  attributes={option.attributes}
                  max={maxSelectableQuantity}
                  stock={stock}
                  value={selfSelectedQuantity}
                  onChange={handleChange(option.id, option.project_code, option.sku)}
                  disabled={disabled}
                  notSelected={notSelected}
                  onIncrement={sendGiftPopupQuantityPlusBtn}
                  onDecrement={sendGiftPopupQuantityMinusBtn}
                />
              )
            })}
          </VStack>
        </VStack>
      </DialogContent>
      <DialogActions rootStyle={styles.dialogActions}>
        {displayRemainingCriterionCount > 0 && (
          <Button disabled={!isSelectedAll && !isReadOnly} fullWidth onClick={handleSubmit} loading={isSubmitting}>
            {t('cartUi.giveawayDialog.nextActionButton', { displayRemainingCriterionCount, defaultValue: '挑選其他好禮（還有 {{displayRemainingCriterionCount}} 個）' })}
          </Button>
        )}
        {displayRemainingCriterionCount <= 0 && (
          <Button disabled={!isSelectedAll && !isReadOnly} fullWidth onClick={handleSubmit} loading={isSubmitting}>
            {isToCheckoutPage ? t('cartUi.confirmAndCheckout', { defaultValue: '確定並結帳' }) : t('cartUi.confirm', { defaultValue: '確定' }) }
          </Button>
        )}
      </DialogActions>
    </>
  )
}

export interface DialogSelectGiveawayProps extends DialogSelectGiveawayContentProps {
  open: boolean
  /**
   * Callback fired when the exit animation has finished.
   */
  onExited?: () => void
}

export const DialogSelectGiveaway = (props: DialogSelectGiveawayProps) => {
  const { open, onClose, onExited, ...rest } = props
  const isMobile = useResponsive('mobile')

  return (
    <ResponsiveDialog
      open={open}
      onClose={onClose}
      onExited={onExited}
      height={isMobile ? 'calc(100vh - 150px)' : undefined}
      maxHeight={520}
      fullWidth
    >
      <DialogSelectGiveawayContent onClose={onClose} {...rest} />
    </ResponsiveDialog>
  )
}
